<script lang="ts">
	import PageGrid16 from '$lib/storyblok/components/pageGrid16.svelte';
	import SbRichtext from '$lib/storyblok/components/sbRichtext.svelte';
	import Image from '$lib/storyblok/components/image.svelte';
	import Video from '$lib/video.svelte';
	import type { SbImage } from '$lib/storyblok/types';
	import Expand from '$lib/icons/expand.svelte';
	import FullscreenVideo from '$lib/fullscreenVideo.svelte';
	import { fade, fly } from 'svelte/transition';
	import Carousel from '$lib/carousel.svelte';
	import { onMount } from 'svelte';
	import Button from '$lib/buttons/v3/button.svelte';
	import type { ISbRichtext, ISbStoryData } from '@storyblok/js';
	import { editable } from '$lib/storyblok/editable';

	interface Props {
		content: ISbStoryData<{
			headline: ISbRichtext;
			subline: ISbRichtext;
			items: Array<
				ISbStoryData<{
					playback_id: string;
					playback_id_desktop: string;
					cover_image: SbImage;
					cover_image_desktop: SbImage;
					name: string;
					subtitle: string;
					quote: string;
				}>['content']
			>;
		}>['content'];
	}

	let { content }: Props = $props();

	let mobileVideos = $state<Array<HTMLVideoElement>>([]);
	let desktopVideos = $state<Array<HTMLVideoElement>>([]);

	let hoveredVideo = $state<number>();
	let expandedVideo = $state<string>();

	let containerWidth = $state(1260);
	let currentIndex = $state(0);
	let activeVideo = $state<number>();

	let mobileVideoTimer: NodeJS.Timeout | number;

	function delayedVideoPlay() {
		clearTimeout(mobileVideoTimer);

		if (window.innerWidth >= 1024) return;

		mobileVideoTimer = setTimeout(async () => {
			activeVideo = currentIndex;
			mobileVideos[activeVideo]?.play();
		}, 2000);
	}

	onMount(() => {
		delayedVideoPlay();

		return () => clearTimeout(mobileVideoTimer);
	});

	// TODO: let's extract the mobile and desktop logic into separate components
</script>

<section
	use:editable={content}
	class="bg-white px-5 py-16 text-brown-1 max-lg:overflow-hidden lg:py-32"
>
	<PageGrid16>
		<div class="col-span-full mb-5 text-center lg:col-start-4 lg:col-end-[14] lg:mb-12">
			<h1
				class="mb-3 text-balance text-[44px]/[112%] tracking-[-.44px] lg:mb-6 lg:text-[64px]/[110%] lg:tracking-[-.64px]"
			>
				<SbRichtext text={content.headline} />
			</h1>
			<div
				class="text-[16px]/[150%] tracking-[-.16px] text-brown-4 lg:text-[20px]/[140%] lg:tracking-[-.2px]"
			>
				<SbRichtext text={content.subline} />
			</div>
		</div>

		<div class="col-span-full lg:hidden">
			<Carousel
				bind:currentIndex
				onchange={() => {
					activeVideo = undefined;
					delayedVideoPlay();
				}}
				class="flex h-full gap-x-4"
				overflow
			>
				{#each content.items as item, i}
					<div
						class="relative aspect-3/4 flex-none"
						style="flex-basis: {content.items.length === 1 ? '100%' : 'calc(100% - 16px)'};"
					>
						{#if item.playback_id?.length > 0}
							<div class="relative z-0 h-full w-full">
								<Video
									autoplay={null}
									bind:videoElement={mobileVideos[i]}
									playbackId={item.playback_id}
									wrapperClass="h-full w-full rounded-2xl"
									class="h-full w-full"
								/>
							</div>
						{/if}
						<div
							class="absolute inset-0 z-10 h-full w-full transition-opacity duration-800"
							class:opacity-0={activeVideo === i && item.playback_id?.length > 0}
						>
							<Image
								class="h-full w-full rounded-2xl object-cover object-center"
								src={item.cover_image.filename}
								alt={item.cover_image.alt}
								title={item.cover_image.title}
							/>
						</div>
						{#if item.name && item.name.length > 0}
							<div
								class="absolute inset-x-0 bottom-0 z-20 whitespace-nowrap px-5 py-4"
								transition:fade
							>
								<h1 class="text-[24px]/[112%] font-semibold tracking-[-.24px] text-white">
									{item.name}
								</h1>
								{#if item.subtitle && item.subtitle.length > 0}
									<p class="text-[14px]/[100%] text-white/[0.76]">
										{item.subtitle}
									</p>
								{/if}
							</div>
						{/if}
						{#if currentIndex === i && item.quote && item.quote.length > 0}
							<div class="absolute inset-0 z-10 rounded-2xl bg-black/[0.08]" transition:fade></div>
							<div class="absolute inset-0 z-20 flex items-start gap-x-4 p-4">
								<div
									class="text-balance p-1 text-[24px]/[112%] tracking-[-.24px] text-white"
									in:fly={{ y: '-50%', duration: 600, delay: 400 }}
								>
									<p class="relative">
										<span class="absolute -left-[0.75ch]">“</span>{item.quote}”
									</p>
								</div>
								<div
									class="rounded-full opacity-0 backdrop-blur-md transition-opacity duration-600"
									class:opacity-100={activeVideo === i}
								>
									<Button type="button" title="Expand video" class="btn-blur">
										<Expand class="text-white" />
									</Button>
								</div>
							</div>
						{/if}
						{#if item.playback_id?.length > 0}
							<button
								class="absolute inset-0 z-20"
								onclick={() => (expandedVideo = item.playback_id)}
								aria-label="Expand video"
							></button>
						{/if}
					</div>
				{/each}
			</Carousel>
		</div>

		<div
			class="relative col-start-2 col-end-[16] flex gap-x-4 max-lg:hidden"
			bind:clientWidth={containerWidth}
		>
			{#each content.items as item, i}
				{@const showContent = hoveredVideo === undefined || hoveredVideo === i}
				{@const video = desktopVideos[i]}
				{@const coverImage = item.cover_image_desktop?.filename
					? item.cover_image_desktop
					: item.cover_image}
				{@const playbackId =
					item.playback_id_desktop?.length > 0 ? item.playback_id_desktop : item.playback_id}
				<article
					class="group relative z-10 flex-1 transition-all duration-600 hover:flex-[2]"
					class:aspect-video={content.items.length === 1}
					class:h-[567px]={content.items.length > 1}
					onpointerenter={() => {
						video?.play();
						hoveredVideo = i;
					}}
					onpointerleave={() => {
						hoveredVideo = undefined;

						if (!video) return;

						video.pause();
						video.currentTime = 0;
					}}
				>
					{#if item.playback_id?.length > 0}
						<div class="relative h-full w-full">
							<Video
								autoplay={null}
								bind:videoElement={desktopVideos[i]}
								{playbackId}
								wrapperClass="h-full w-full rounded-2xl"
								class="h-full w-full"
								objectPosition={content.items.length > 1 ? 'calc(50% - 26px) center' : '50% 50%'}
							/>
						</div>
						<div
							class="absolute right-5 top-5 z-30 rounded-full opacity-0 backdrop-blur-md transition-opacity duration-600 group-hover:opacity-100"
						>
							<Button
								type="button"
								title="Expand video"
								onclick={() => (expandedVideo = playbackId)}
								class="btn-blur"
							>
								<Expand class="text-white" />
							</Button>
						</div>
					{/if}
					<Image
						class="absolute inset-0 z-0 h-full w-full rounded-2xl object-cover object-center transition-opacity duration-300 {playbackId.length >
						0
							? 'group-hover:opacity-0'
							: ''}"
						src={coverImage.filename}
						alt={coverImage.alt}
						title={coverImage.title}
					/>
					{#if showContent && item.name && item.name.length > 0}
						<div
							class="absolute inset-x-0 bottom-0 z-20 whitespace-nowrap px-6 py-4"
							transition:fade
						>
							<h1 class="text-[32px]/[110%] font-semibold tracking-[-.32px] text-white">
								{item.name}
							</h1>
							{#if item.subtitle && item.subtitle.length > 0}
								<p class="text-[20px]/[140%] tracking-[-.2px] text-white/[0.76]">{item.subtitle}</p>
							{/if}
						</div>
					{/if}
					{#if hoveredVideo === i && item.quote && item.quote.length > 0}
						<div
							class="absolute left-0 top-0 z-20 max-w-[460px] text-balance text-[32px]/[110%] tracking-[-.32px] text-white"
							in:fly={{ y: '-50%', duration: 600 }}
							style="width: {containerWidth / 3 + 32}px"
						>
							<p class="relative p-6">
								<span class="absolute left-[0.5ch]">“</span>{item.quote}”
							</p>
						</div>
					{/if}

					{#if playbackId.length > 0}
						<button
							type="button"
							onclick={() => (expandedVideo = playbackId)}
							class="absolute inset-0 z-20"
							aria-label="Expand video"
						></button>
					{/if}
				</article>
			{/each}
		</div>
	</PageGrid16>
</section>

{#if expandedVideo !== undefined}
	<FullscreenVideo
		backgroundWithOpacity
		buttonPos="top-right"
		playbackId={expandedVideo}
		onclose={() => (expandedVideo = undefined)}
	/>
{/if}
